import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Star as StarIcon, ArrowLeft, ArrowRight, CheckCircle2, Circle, Loader2 } from "lucide-react";
import api from "../../lib/api";
import useSeo from "../../hooks/useSeo";

export default function StarsDetail() {
    const { star } = useParams();
    const nav = useNavigate();
    const { i18n } = useTranslation();
    const lang = (i18n.language || "tr").split("-")[0];
    const starNum = parseInt(star, 10);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    useEffect(() => {
        if (!starNum || starNum < 1 || starNum > 5) {
            setErr("Geçersiz yıldız seviyesi");
            setLoading(false);
            return;
        }
        api.get(`/stars/${starNum}`)
            .then(({ data }) => setData(data))
            .catch(() => setErr("Yüklenemedi"))
            .finally(() => setLoading(false));
    }, [starNum]);

    const tr = (obj) => (obj && (obj[lang] || obj.tr)) || "";

    useSeo({
        title: data?.seo?.meta_title || `${starNum} Yıldız Otel Standartları`,
        description: data?.seo?.meta_desc || `${starNum} yıldızlı oteller için zorunlu kriterler ve kategoriler.`,
        path: `/yildiz-standartlari/${starNum}`,
        structuredData: data
            ? {
                  "@context": "https://schema.org",
                  "@type": "TechArticle",
                  headline: data.seo?.meta_title || `${starNum} Yıldız Otel Standartları`,
                  description: data.seo?.meta_desc || "",
              }
            : undefined,
    });

    return (
        <div className="bg-light-bg min-h-[80vh]" data-testid="stars-detail-page">
            <section className="bg-dark-navy text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#08111d] via-[#0c2438] to-[#123b52]" />
                <div className="relative max-w-5xl mx-auto px-6 py-12 lg:py-16">
                    <Link to="/yildiz-standartlari" className="text-xs uppercase tracking-[0.2em] text-teal font-semibold inline-flex items-center gap-1 hover:text-white">
                        <ArrowLeft className="w-3.5 h-3.5" /> Tüm Yıldız Standartları
                    </Link>
                    <div className="mt-3 font-data text-5xl text-gold">{Array.from({ length: starNum || 0 }).map(() => "★").join("")}</div>
                    <h1 className="mt-2 text-4xl sm:text-5xl font-bold tracking-tight">{starNum} Yıldız Otel Standartları</h1>
                    {data?.general_description && (
                        <p className="mt-3 text-white/75 max-w-2xl">{tr(data.general_description)}</p>
                    )}
                </div>
            </section>

            <section className="max-w-5xl mx-auto px-6 py-12">
                {loading ? (
                    <div className="text-center text-deep-blue/60 text-sm py-12 inline-flex items-center justify-center gap-2 w-full">
                        <Loader2 className="w-5 h-5 animate-spin" /> Yükleniyor...
                    </div>
                ) : err ? (
                    <div className="text-center py-12">
                        <h2 className="text-xl text-data-red font-semibold">{err}</h2>
                        <Link to="/yildiz-standartlari" className="mt-3 inline-flex items-center gap-2 text-teal hover:text-deep-blue">
                            Geri dön <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                ) : (
                    <div className="space-y-6" data-testid="stars-detail-categories">
                        {(data?.categories || []).map((cat, ci) => (
                            <div key={ci} className="bg-white rounded-lg border border-slate-100 shadow-md p-6" data-testid={`stars-category-${ci}`}>
                                <h2 className="text-lg font-bold text-dark-navy">{tr(cat.name)}</h2>
                                <div className="mt-3 space-y-2">
                                    {(cat.requirements || []).map((req, ri) => (
                                        <div key={ri} className="flex items-start gap-3 p-3 rounded-md bg-light-bg border border-slate-100">
                                            {req.is_mandatory ? (
                                                <CheckCircle2 className="w-4 h-4 text-data-green mt-0.5 shrink-0" />
                                            ) : (
                                                <Circle className="w-4 h-4 text-deep-blue/40 mt-0.5 shrink-0" />
                                            )}
                                            <div className="flex-1">
                                                <p className="text-sm text-deep-blue/85">{tr(req.text)}</p>
                                                <div className="mt-1 flex items-center gap-2 text-[11px] text-deep-blue/60">
                                                    <span className={`inline-block px-1.5 py-0.5 rounded font-semibold ${req.is_mandatory ? "bg-data-green/10 text-data-green" : "bg-slate-200 text-slate-600"}`}>
                                                        {req.is_mandatory ? "Zorunlu" : "Opsiyonel"}
                                                    </span>
                                                    {req.source_reference && <span>· {req.source_reference}</span>}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            <section className="bg-white py-10 border-t border-slate-200">
                <div className="max-w-5xl mx-auto px-6 flex items-center justify-between gap-3 flex-wrap">
                    {starNum > 1 && (
                        <button
                            onClick={() => nav(`/yildiz-standartlari/${starNum - 1}`)}
                            className="inline-flex items-center gap-2 text-sm font-semibold text-deep-blue hover:text-dark-navy"
                            data-testid="stars-prev"
                        >
                            <ArrowLeft className="w-4 h-4" /> {starNum - 1} Yıldız
                        </button>
                    )}
                    <Link
                        to="/yildiz-standartlari/kontrol"
                        className="inline-flex items-center gap-2 bg-gold hover:bg-gold-700 text-dark-navy font-bold text-sm px-5 py-3 rounded-md shadow-md"
                    >
                        Kaç Yıldızı Hak Ediyorsunuz?
                    </Link>
                    {starNum < 5 && (
                        <button
                            onClick={() => nav(`/yildiz-standartlari/${starNum + 1}`)}
                            className="inline-flex items-center gap-2 text-sm font-semibold text-deep-blue hover:text-dark-navy"
                            data-testid="stars-next"
                        >
                            {starNum + 1} Yıldız <ArrowRight className="w-4 h-4" />
                        </button>
                    )}
                </div>
            </section>
        </div>
    );
}
